<template>
  <div class="transfer-stock-create">
    <div class="d-flex align-items-center">
      <div>
        <h1 class="h3 d-flex align-items-center">
          <a-button type="link" size="small" class="mr-2" @click="goBackToIndex">
            <a-icon type="left" />
          </a-button>
          {{ $t('transferStock.createTitle') }}
        </h1>
      </div>
    </div>

    <a-form-model :model="form" :rules="rules">
      <a-card :title="$t('transferStock.labels.detailCardTitle')" class="w-100 mt-4" :bordered="true">
        <a-row :gutter="24">
          <a-col :span="12">
            <a-form-model-item :label="$t('transferStock.labels.numberLabel')" prop="number">
              <a-input
                v-model.trim="form.number"
                autocomplete="off"
                :placeholder="$t('transferStock.labels.numberLabel')"
                allow-clear
                :read-only="true"
              />
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item :label="$t('transferStock.labels.dateLabel')" prop="date">
              <a-date-picker
                v-model="form.date"
                class="w-100"
                :format="dateFormat"
                :disabled-date="disabledDate"
              />
            </a-form-model-item>
          </a-col>
        </a-row>
        <a-row :gutter="24">
          <a-col :span="12">
            <a-form-model-item :label="$t('transferStock.labels.warehouseFromLabel')" prop="source_warehouse_id" class="mb-0">
              <a-select
                v-model="form.source_warehouse_id"
                class="select-antd-default"
                :class="{ 'locked-select': isSourceDisabled }"
                :placeholder="$t('transferStock.optionGudang')"
                show-search
                :disabled="isSourceDisabled"
                :filter-option="filterWarehouseOption"
                @change="onChangeSourceWarehouse"
              >
                <a-icon v-if="isSourceDisabled" slot="suffixIcon" type="lock" />
                <a-select-option v-for="item in sourceWarehouseOptions" :key="item.value" :value="item.value">
                  {{ item.label }}
                </a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <a-form-model-item :label="$t('transferStock.labels.warehouseToLabel')" prop="target_warehouse_id" class="mb-0">
              <a-select
                v-model="form.target_warehouse_id"
                class="select-antd-default"
                :placeholder="$t('transferStock.optionGudang')"
                show-search
                :filter-option="filterWarehouseOption"
                @change="onChangeTargetWarehouse"
              >
                <a-select-option v-for="item in targetWarehouseOptions" :key="item.value" :value="item.value">
                  {{ item.label }}
                </a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
        </a-row>
        <a-row :gutter="24" class="mt-2">
          <a-col :span="24">
            <a-form-model-item :label="$t('transferStock.labels.noteLabel')" prop="note">
              <a-textarea v-model.trim="form.note" :auto-size="{ minRows: 5, maxRows: 6 }" :placeholder="$t('transferStock.placeholders.noteOptional')" />
            </a-form-model-item>
          </a-col>
        </a-row>
      </a-card>

      <a-card :title="$t('transferStock.labels.itemCardTitle')" class="w-100 mt-4" :bordered="true">
        <a-table
          :data-source="itemList"
          :row-key="record => record._row_id"
          :columns="columns"
          :pagination="false"
          :scroll="{ x: 1000 }"
          size="middle"
        >
          <template slot="no" slot-scope="text, record, index">
            {{ index + 1 }}
          </template>
          <template slot="catalog" slot-scope="text, record">
            <div v-if="record.catalog_id && !record._editingCatalog" class="catalog-display-line" @click="record._editingCatalog = true">
              <span class="sku">{{ record.sku }}</span> - <span class="title">{{ record.title }}</span>
              <span :class="['pill','pill-avail', (Number(record.availability || 0) < 0 ? 'pill-error' : '')]">{{ record.availability }} - {{ record.unit }}</span>
            </div>
            <a-select
              v-else
              :ref="`sel_${record._row_id}`"
              v-model="record.catalog_val"
              class="select-antd-default"
              :placeholder="$t('transferStock.placeholders.selectProduct')"
              show-search
              :label-in-value="false"
              :filter-option="false"
              :not-found-content="fetching ? undefined : null"
              :disabled="!form.source_warehouse_id || !form.target_warehouse_id"
              style="min-width: 280px"
              @search="onSearchCatalog"
              @focus="() => onFocusCatalog(record)"
              @change="(val) => onChangeCatalog(record, val)"
              @blur="() => onBlurCatalog(record)"
            >
              <a-spin v-if="fetching" slot="notFoundContent" />
              <a-select-option
                v-for="item in catalogOptions"
                :key="item.id"
                :value="`${item.id}.${item.product_id}`"
                :disabled="selectedCatalogIds.includes(item.id) || Number(item.availability || 0) <= 0"
              >
                <div class="option-item">
                  <div class="option-line">
                    <span class="sku">{{ item.sku?.toUpperCase() || '-' }}</span>
                    <span class="title">{{ item.title }}</span>
                    <span class="pill pill-meta">{{ item.availability }} - {{ item.unit || 'PCS' }}</span>
                  </div>
                </div>
              </a-select-option>
            </a-select>
          </template>
          <template slot="quantity" slot-scope="text, record">
            <InputNumber
              :ref="`qty_${record._row_id}`"
              v-model="record.quantity"
              :addon="record.unit"
              :class="{ 'qty-error': record._qty_error }"
              @change="(val) => onChangeQuantity(val, record)"
              @keyup.enter.native="onPressEnterQty(record)"
            />
          </template>
          <template slot="action" slot-scope="text, record">
            <a-icon type="delete" style="color: #E00000; cursor: pointer" @click="removeItem(record)" />
          </template>
          <template slot="footer">
            <div class="stock-transfer d-flex justify-content-start table-footer-ctrl">
              <a-button type="primary" @click="addRowAndFocus">
                <a-icon type="plus" /> {{ $t('transferStock.buttons.addRow') }}
              </a-button>
            </div>
          </template>
        </a-table>
      </a-card>
      <div class="mt-3 d-flex align-items-center">
        <div>
          <a-button type="default" @click="goBackToIndex">
            <a-icon type="left" />
          </a-button>
        </div>
        <div class="ml-auto text-right">
          <a-button
            type="primary"
            ghost
            class="mr-3"
            :disabled="loadingSubmit"
            @click="cancel"
          >
            {{ $t('utils.cancel') }}
          </a-button>
          <a-button
            type="primary"
            :loading="loadingSubmit"
            :disabled="!canSubmit"
            @click="submitTransfer"
          >
            <a-icon type="save" />
            {{ $t('transferStock.buttons.saveTransfer') }}
          </a-button>
        </div>
      </div>
    </a-form-model>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import { getTransferWarehouseCatalogList, createTransferNonWms } from '@/api/inventory'
import { getWarehouseListByWorkspace, getWarehouseInternal } from '@/api/warehouse'
import { useInputNumber } from '@/composable/useInputNumber'
import InputNumber from '@/components/Input/InputNumber.vue'

export default {
  name: 'TransferStockCreate',
  components: {
    InputNumber,
  },
  setup() {
    const { toNumber } = useInputNumber()
    return { toNumber }
  },
  data() {
    return {
      dateFormat: 'DD MMM YYYY',
      form: {
        number: '',
        date: null,
        source_warehouse_id: undefined,
        target_warehouse_id: undefined,
        note: '',
      },
      rules: {
        number: [{ required: true, message: 'Nomor transfer wajib diisi' }],
        date: [{ required: true, message: 'Tanggal wajib diisi' }],
        source_warehouse_id: [{ required: true, message: 'Gudang asal wajib diisi' }],
        target_warehouse_id: [{ required: true, message: 'Gudang tujuan wajib diisi' }],
      },
      warehouseList: [],
      sourceWarehouseLabel: '',
      sourceWarehouse: null,
      fetching: false,
      catalogOptions: [],
      itemList: [],
      columns: [
        { title: 'No', dataIndex: 'no', width: 60, align: 'center', scopedSlots: { customRender: 'no' } },
        { title: 'SKU/Catalog', dataIndex: 'catalog', scopedSlots: { customRender: 'catalog' } },
        { title: 'Qty Request', dataIndex: 'quantity', width: 200, align: 'right', scopedSlots: { customRender: 'quantity' } },
        { title: 'Aksi', dataIndex: 'action', width: 100,  scopedSlots: { customRender: 'action' }, align: 'center' },
      ],
      loadingSubmit: false,
      previousSourceId: undefined,
      previousTargetId: undefined,
    }
  },
  computed: {
    businessId() {
      return this.$store.state.user.restriction_base === 'Warehouse'
        ? Object.keys(this.$store.getters['user/businessListObjectByKey'])[0]
        : this.$route.query.business_id
    },
    restriction() {
      return this.$store.state.user.restriction_base
    },
    isSourceDisabled() {
      return !!(this.$route.query.warehouse_id || this.$route.query.warehouseId) || this.restriction === 'Warehouse'
    },
    canSubmit() {
      const hasHeader = (
        this.form.number &&
        this.form.date &&
        this.form.source_warehouse_id &&
        this.form.target_warehouse_id
      )
      let hasValidItems = false
      let allQtyFilled = true
      let hasQtyOnly = false
      for (const r of this.itemList) {
        const hasCatalog = !!r.catalog_id
        const hasQty = r.quantity != null && r.quantity !== ''
        if (hasCatalog) {
          hasValidItems = true
          const qty = this.toNumber(r.quantity ?? 0)
          if (!(qty > 0)) allQtyFilled = false
        } else if (hasQty) {
          hasQtyOnly = true
          break
        }
      }
      return hasHeader && hasValidItems && allQtyFilled && !hasQtyOnly
    },
    targetWarehouseOptions() {
      const sourceId = String(this.form.source_warehouse_id)
      let list = this.warehouseList || []

      if (this.restriction === 'Warehouse') {
        const allowedWarehouseIds = (this.$store.state.user.businessList || [])
          .reduce((acc, b) => {
            if (b.warehouse_id) acc.push(String(b.warehouse_id))
            if (b.id) acc.push(String(b.id))
            return acc
          }, [])

        list = list.filter(w => allowedWarehouseIds.includes(String(w.value)))
      }

      return list.filter(w => {
        const rawCode = w?.wms_code
        // Consider it non-WMS if the code is genuinely empty, "null" string, or "N/A"
        const isNonWms = !rawCode || 
                         String(rawCode).trim() === '' || 
                         String(rawCode).trim().toUpperCase() === 'N/A' || 
                         String(rawCode).trim().toLowerCase() === 'null' ||
                         String(rawCode).trim().toLowerCase() === 'undefined'
        
        return isNonWms && String(w.value) !== sourceId
      })
    },
    sourceWarehouseOptions() {
      if (this.restriction === 'Business') {
        return (this.warehouseList || [])
      }
      const sid = String(this.form.source_warehouse_id)
      return (this.warehouseList || []).filter(w => String(w.value) === sid)
    },
    selectedCatalogIds() {
      return this.itemList
        .map(r => r.catalog_id)
        .filter(Boolean)
    },
  },
  watch: {
    form: {
      deep: true,
      handler() {},
    },
  },
  created() {
    this.form.number = this.generateTransferNumber()
    this.form.date = this.$moment()
    const sourceId = this.$route.query.warehouse_id || this.$route.query.warehouseId
    this.form.source_warehouse_id = sourceId ? String(sourceId) : undefined
    this.previousSourceId = this.form.source_warehouse_id
    this.previousTargetId = this.form.target_warehouse_id
    this.fetchWarehouses()
  },
  methods: {
    disabledDate(current) {
      return false
    },
    generateTransferNumber() {
      const now = this.$moment()
      const seq = String(now.diff(now.clone().startOf('day'))).padStart(8, '0')
      return `TWH-${seq}`
    },
    async fetchWarehouses() {
      this.isLoading = true
      try {
        // Fetch complete warehouse list for lookup
        const { data: response } = await getWarehouseInternal({
          business_id: this.businessId,
          limit: 500,
        })
        const rawList = response?.data?.data || response?.data || []
        const apiList = rawList.map(w => ({
          ...w,
          value: String(w.id || w.warehouse_id || w.value),
          label: String(w.name || w.label || w.warehouse_name || '').trim(),
        }))

        // Sync with Header (businessList) if Warehouse restricted
        if (this.restriction === 'Warehouse') {
          const storeList = (this.$store.state.user.businessList || []).map(b => {
            const sid = String(b.warehouse_id)
            const apiData = apiList.find(api => String(api.value) === sid)
            return {
              ...b,
              ...(apiData || {}),
              value: sid,
              label: String(b.name || apiData?.label || '').trim(),
              wms_code: apiData ? apiData.wms_code : null,
            }
          })
          this.warehouseList = storeList
        } else {
          this.warehouseList = apiList
        }

        const sid = this.$route.query.warehouse_id || this.$route.query.warehouseId || this.form.source_warehouse_id
        const src = this.warehouseList.find(w => w.value === String(sid))

        if (src) {
          this.form.source_warehouse_id = src.value
          this.previousSourceId = src.value
          this.sourceWarehouse = src
          this.sourceWarehouseLabel = src.label
        } else {
          // Fallback to sid if not in primary list
          this.form.source_warehouse_id = sid ? String(sid) : this.form.source_warehouse_id
          this.previousSourceId = this.form.source_warehouse_id
        }
        if (this.form.source_warehouse_id && this.form.target_warehouse_id) {
          await this.fetchCatalogOptions('')
        }
      } catch (err) {
        console.error('fetchWarehouses error:', err)
        this.warehouseList = []
      } finally {
        this.isLoading = false
      }
    },
    onChangeTargetWarehouse(value) {
      const hasFilledItems = this.itemList.some(item => !!item.catalog_id || (item.quantity != null && item.quantity !== ''))
      if (hasFilledItems) {
        this.$confirm({
          title: this.$t('transferStock.confirm.changeWarehouseTitle'),
          content: this.$t('transferStock.confirm.changeWarehouseContent'),
          okText: this.$t('utils.yes'),
          cancelText: this.$t('utils.no'),
          onOk: () => {
            this.handleTargetWarehouseChange(value)
          },
          onCancel: () => {
            this.form.target_warehouse_id = this.previousTargetId
          },
        })
      } else {
        this.handleTargetWarehouseChange(value)
      }
    },
    handleTargetWarehouseChange(value) {
      this.previousTargetId = value
      // Reset item-related state when target changes
      this.catalogOptions = []
      this.itemList = []
      if (this.form.source_warehouse_id && value) {
        this.fetchCatalogOptions('')
      }
    },
    onChangeSourceWarehouse(value) {
      const hasFilledItems = this.itemList.some(item => !!item.catalog_id || (item.quantity != null && item.quantity !== ''))
      if (hasFilledItems) {
        this.$confirm({
          title: this.$t('transferStock.confirm.changeWarehouseTitle'),
          content: this.$t('transferStock.confirm.changeWarehouseContent'),
          okText: this.$t('utils.yes'),
          cancelText: this.$t('utils.no'),
          onOk: () => {
            this.handleSourceWarehouseChange(value)
          },
          onCancel: () => {
            this.form.source_warehouse_id = this.previousSourceId
          },
        })
      } else {
        this.handleSourceWarehouseChange(value)
      }
    },
    handleSourceWarehouseChange(value) {
      this.previousSourceId = value
      const src = this.warehouseList.find(w => String(w.value) === String(value))
      this.sourceWarehouse = src || null
      this.sourceWarehouseLabel = src?.label || ''
      // Reset item-related state when source changes
      this.catalogOptions = []
      this.itemList = []
      // Reset target if equals source
      if (String(this.form.target_warehouse_id) === String(value)) {
        this.form.target_warehouse_id = undefined
        this.previousTargetId = undefined
      }
    },
    filterWarehouseOption(input, option) {
      return option.componentOptions.children[0].text.toLowerCase().includes(input.toLowerCase())
    },
    onSearchCatalog: debounce(async function (value) {
      if (!this.form.source_warehouse_id || !this.form.target_warehouse_id) return
      this.fetching = true
      try {
        await this.fetchCatalogOptions(value)
      } catch (e) {
        this.catalogOptions = []
      } finally {
        this.fetching = false
      }
    }, 500),
    async fetchCatalogOptions(search) {
      const params = {
        business_id: this.businessId,
        source_warehouse_id: this.form.source_warehouse_id,
        destination_warehouse_id: this.form.target_warehouse_id,
        search: search || '',
        page: 1,
        limit: 20,
      }
      const { data: { data: response } } = await getTransferWarehouseCatalogList(params)
      this.catalogOptions = (response || []).map(item => ({
        id: item.id,
        product_id: item.product_id,
        sku: item.sku,
        title: item.title,
        availability: item.availability,
        unit: item.unit || 'PCS',
        images: item.images,
      }))
    },
    addRow() {
      const id = `${Date.now()}-${Math.floor(Math.random()*1000)}`
      this.itemList.push({ _row_id: id, catalog_val: '', catalog_id: null, sku: '', title: '', availability: 0, quantity: null, unit: 'PCS', note: '', _editingCatalog: true })
    },
    addRowAndFocus() {
      this.addRow()
      const record = this.itemList[this.itemList.length - 1]
      this.$nextTick(() => {
        this.focusSelect(record)
        this.scrollLastRowIntoView()
      })
    },
    async onPickCatalog(record, value) {
      const selected = this.catalogOptions.find(o => `${o.id}.${o.product_id}` === value)
      record.catalog_val = value
      if (selected) {
        record.catalog_id = selected.id
        record.sku = selected.sku?.toUpperCase() || '-'
        record.title = selected.title
        record.availability = selected.availability
        record.unit = selected.unit || 'PCS'
        record.quantity = null
        record._avail_error = Number(selected.availability ?? 0) < 0
        if (record._avail_error) {
          this.$message.error(this.$t('transferStock.error.availabilityNegative'))
        }
        record._editingCatalog = false
        this.$nextTick(() => this.focusQty(record))
      }
    },
    async onFocusCatalog(record) {
      if (!this.form.source_warehouse_id || !this.form.target_warehouse_id) return
      if (!this.catalogOptions.length) {
        await this.fetchCatalogOptions('')
      }
    },
    async onChangeCatalog(record, val) {
      if (!val) {
        // cleared
        record.catalog_val = ''
        record.catalog_id = null
        record.sku = ''
        record.title = ''
        record.availability = 0
        record.quantity = null
        record._qty_error = false
        record._avail_error = false
        record._editingCatalog = true
        return
      }
      await this.onPickCatalog(record, val)
    },
    onBlurCatalog(record) {
      if (record.catalog_id) {
        record._editingCatalog = false
      }
    },
    onChangeQuantity(value, record) {
      record.note = ''
      const qty = this.toNumber(value)
      if (!(qty > 0)) {
        record._qty_error = true
        this.$message.error('Qty harus lebih besar dari 0')
        return
      }
      const avail = Number(record.availability ?? 0)
      if (Number.isFinite(qty) && Number.isFinite(avail)) {
        record._qty_error = qty > avail || Number(avail) < 0
        if (record._qty_error) {
          if (Number(avail) < 0) {
            this.$message.error(this.$t('transferStock.error.availabilityNegative'))
          } else {
            this.$message.warning(this.$t('transferStock.error.qtyExceeded'))
          }
        }
      } else {
        record._qty_error = false
      }
    },
    focusQty(record) {
      const ref = this.$refs[`qty_${record._row_id}`]
      try {
        if (ref && ref.$el) {
          const input = ref.$el.querySelector('input')
          if (input) input.focus()
        }
      } catch (e) {}
    },
    focusSelect(record) {
      const sel = this.$refs[`sel_${record._row_id}`]
      try {
        if (sel && typeof sel.focus === 'function') {
          sel.focus()
        } else if (sel && sel.$el) {
          const input = sel.$el.querySelector('input')
          if (input) input.focus()
        }
      } catch (e) {}
    },
    scrollLastRowIntoView() {
      try {
        const rows = document.querySelectorAll('.ant-table-body .ant-table-row')
        const last = rows[rows.length - 1]
        if (last) last.scrollIntoView({ behavior: 'smooth', block: 'center' })
      } catch (e) {}
    },
    onPressEnterQty(record) {
      this.addRowAndFocus()
    },
    goBackToIndex() {
      this.$router.push({ name: 'inventory.transfer.stock', query: { ...this.$route.query } })
    },
    removeItem(record) {
      this.itemList = this.itemList.filter(i => i._row_id !== record._row_id)
    },
    cancel() {
      this.$router.push({ name: 'inventory.transfer.stock', query: { ...this.$route.query } })
    },
    async submitTransfer() {
      const sourceId = this.sourceWarehouse?.value || this.form.source_warehouse_id
      const targetId = this.form.target_warehouse_id
      if (!sourceId || !targetId) {
        this.$message.error('Gudang asal dan tujuan wajib diisi')
        return
      }
      if (String(sourceId) === String(targetId)) {
        this.$message.error('Gudang tujuan harus berbeda dengan gudang asal')
        return
      }
      const validItems = this.itemList.filter(r => r.catalog_id)
      if (!validItems.length) {
        this.$message.error('Minimal 1 item valid diperlukan')
        return
      }
      const qtyOnlyRow = this.itemList.find(r => (r.quantity != null && r.quantity !== '') && !r.catalog_id)
      if (qtyOnlyRow) {
        this.$message.error('Pilih produk terlebih dahulu untuk baris dengan Qty')
        this.$nextTick(() => this.focusSelect(qtyOnlyRow))
        return
      }
      let emptyQty = false
      let firstEmpty = null
      let zeroQty = false
      let firstZero = null
      let negativeAvail = false
      let negativeQty = false
      let exceeded = false
      let firstExceeded = null
      for (const r of validItems) {
        const isEmpty = r.quantity == null || r.quantity === ''
        if (isEmpty) {
          emptyQty = true
          if (!firstEmpty) firstEmpty = r
          r._qty_error = true
          continue
        }
        const avail = Number(r.availability ?? 0)
        const qty = this.toNumber(r.quantity ?? 0)
        if (qty <= 0) {
          zeroQty = true
          if (!firstZero) firstZero = r
          r._qty_error = true
          continue
        }
        if (avail < 0) negativeAvail = true
        if (qty < 0) negativeQty = true
        if (qty > avail) {
          exceeded = true
          if (!firstExceeded) firstExceeded = r
        }
        if (negativeAvail && negativeQty && exceeded && emptyQty && zeroQty) break
      }
      if (emptyQty) {
        const skuInfo = firstEmpty?.sku ? ` (SKU ${firstEmpty.sku})` : ''
        this.$message.error('Qty wajib diisi' + skuInfo)
        this.$nextTick(() => this.focusQty(firstEmpty))
        return
      }
      if (zeroQty) {
        const skuInfo = firstZero?.sku ? ` (SKU ${firstZero.sku})` : ''
        this.$message.error('Qty harus lebih besar dari 0' + skuInfo)
        this.$nextTick(() => this.focusQty(firstZero))
        return
      }
      if (negativeAvail) {
        this.$message.error(this.$t('transferStock.error.availabilityNegativeOnSubmit'))
        return
      }
      if (negativeQty) {
        this.$message.error(this.$t('transferStock.error.qtyNegative'))
        return
      }
      if (exceeded) {
        const skuInfo = firstExceeded?.sku ? ` (SKU ${firstExceeded.sku})` : ''
        this.$message.error(this.$t('transferStock.error.qtyExceededOnSubmit') + skuInfo)
        return
      }
      const data = {
        number: this.form.number,
        date: this.$moment(this.form.date).format('YYYY-MM-DDTHH:mm:ssZ'),
        note: this.form.note || '',
        source_warehouse_id: String(sourceId),
        source_business_id: this.businessId,
        destination_warehouse_id: String(targetId),
        destination_business_id: this.businessId,
        line_items: validItems.map(r => ({
          catalog_id: String(r.catalog_id),
          catalog_title: r.title,
          quantity: this.toNumber(r.quantity ?? 0),
          unit: r.unit || 'PCS',
          sku: r.sku,
        })),
      }
      this.loadingSubmit = true
      try {
        const { data: res } = await createTransferNonWms({ business_id: this.businessId, data })
        const ok = String(res?.message || '').toLowerCase() === 'success'
        if (ok) {
          this.$message.success(`Transfer Stok Tersimpan — Nomor: ${this.form.number}`, 4)
          // Clear all filters except warehouse after successful save
          const preservedQuery = {
            business_id: this.$route.query.business_id,
            warehouse_id: this.$route.query.warehouse_id,
            warehouseId: this.$route.query.warehouseId,
          }
          // Remove undefined values
          Object.keys(preservedQuery).forEach(key => {
            if (preservedQuery[key] === undefined) delete preservedQuery[key]
          })
          this.$router.push({ name: 'inventory.transfer.stock', query: preservedQuery })
        } else {
          const msg = res?.message || 'Gagal membuat transfer stok'
          this.$message.error(msg)
        }
      } catch (e) {
        console.error('transfer-save error', e?.response?.data || e)
        const msg = e?.response?.data?.message || e?.message || 'Gagal membuat transfer stok'
        this.$message.error(msg)
      } finally {
        this.loadingSubmit = false
      }
    },
  },
}
</script>


<style lang="scss" scoped>
.product-select {
  .sku-desc {
    color: #999;
    font-size: 12px;
  }
}
.select-antd-default.ant-select-lg .ant-select-selection__rendered {
  line-height: 38px !important;
}
.readonly-select {
  pointer-events: none;
}
.locked-select {
  &.ant-select-disabled {
    ::v-deep .ant-select-selection {
      background-color: #fff !important;
      border-color: #d9d9d9 !important;
      cursor: default;
      .ant-select-selection-selected-value {
        color: rgba(0, 0, 0, 0.65) !important;
        opacity: 1 !important;
      }
    }
  }
}
.table-footer-ctrl {
  padding-left: 0 !important;
  background: transparent !important;
}
.catalog-display-line {
  cursor: pointer;
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.catalog-display-line .sku {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-weight: 600;
}
.catalog-display-line .title {
  color: #333;
}
.pill {
  display: inline-block;
  padding: 0 8px;
  line-height: 20px;
  border-radius: 12px;
  font-size: 12px;
  margin-left: 8px;
}
.catalog-display-line .pill {
  margin-left: auto;
}
.ant-select-selection-selected-value .option-line {
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
}
.ant-select-selection-selected-value .pill {
  margin-left: auto;
  padding: 0 8px;
  line-height: 20px;
}
.pill-qty {
  background: #e6f7ff;
  color: #096dd9;
}
.pill-unit {
  background: #f6ffed;
  color: #389e0d;
}
.pill-avail {
  background: #e6f7ff;
  color: #096dd9;
}
.pill-error {
  background: #fff1f0;
  color: #cf1322;
}
.qty-error {
  border: 1px solid #ff4d4f;
  border-radius: 4px;
}
.catalog-display {
  cursor: pointer;
}
.catalog-display .sku-desc {
  color: #999;
  font-size: 12px;
}
.option-item {
  display: flex;
  flex-direction: column;
}
.option-line {
  display: flex;
  gap: 8px;
}
.option-line .sku {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-weight: 600;
}
.option-line .title {
  color: #333;
}
.pill-meta {
  background: #e6f7ff;
  color: #096dd9;
}
.ant-select-selection-selected-value .pill-meta {
  margin-left: auto;
}
.option-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #666;
}
.option-meta .uom {
  background: #f0f0f0;
  border-radius: 3px;
  padding: 2px 6px;
}
.option-meta .dot {
  color: #999;
}
</style>
<style lang="scss">
.transfer-stock-create {
  .ant-table-footer {  
    padding-left: 0 !important;
    padding-right: 0 !important;
    padding-bottom: 0 !important;
    background: transparent !important;
  }
  .ant-table-footer > div {
    padding-left: 0 !important;
    padding-right: 0 !important;
    padding-bottom: 0 !important;
    background: transparent !important;
  }
}
.ant-select-dropdown-menu-item .option-item {
  display: block;
}
.ant-select-dropdown-menu-item .option-line {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ant-select-dropdown-menu-item .option-line .sku {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-weight: 600;
}
.ant-select-dropdown-menu-item .option-line .title {
  color: #333;
}
.ant-select-dropdown-menu-item .pill {
  display: inline-block;
  padding: 0 8px;
  line-height: 18px;
  border-radius: 12px;
  font-size: 12px;
}
.ant-select-dropdown-menu-item .pill-meta {
  background: #e6f7ff;
  color: #096dd9;
  margin-left: auto;
}
.ant-select-dropdown-menu-item .pill-unit {
  background: #f6ffed;
  color: #389e0d;
}
.ant-select-dropdown-menu-item .pill-avail {
  background: #fafafa;
  color: #595959;
}
.ant-select-dropdown-menu-item .dot {
  color: #999;
}
</style>
